import { useState, useCallback, useEffect, useRef } from "react";
import { supabase } from "@/lib/supabaseClient";
import { Project, ExtendedProject } from "@/types/project";
import { Task } from "@/services/taskService";

// ============================================================================
// Types
// ============================================================================

/** Options for the useProjectData hook */
export interface UseProjectDataOptions {
  projectId: string | null;
  isOpen: boolean;
  /** Callback to update form state when project is loaded */
  onProjectLoad?: (project: ExtendedProject) => void;
  /** Callback when error occurs */
  onError?: (error: Error) => void;
}

/** Return type for the useProjectData hook */
export interface UseProjectDataReturn {
  // Core data
  project: Project | null;
  setProject: React.Dispatch<React.SetStateAction<Project | null>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  contentLoaded: boolean;
  setContentLoaded: React.Dispatch<React.SetStateAction<boolean>>;

  // Tasks data
  openTasks: Task[];
  setOpenTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  completedTasks: Task[];
  setCompletedTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  assigneeNames: Record<string, string>;
  setAssigneeNames: React.Dispatch<React.SetStateAction<Record<string, string>>>;

  // Recently saved protection
  recentlySaved: boolean;
  setRecentlySaved: React.Dispatch<React.SetStateAction<boolean>>;
  recentlySavedTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;

  // Fetching guards
  isFetchingProject: React.MutableRefObject<boolean>;
  fetchProjectTimeoutId: NodeJS.Timeout | null;
  setFetchProjectTimeoutId: React.Dispatch<React.SetStateAction<NodeJS.Timeout | null>>;

  // Modal state tracking
  hasOpened: boolean;
  setHasOpened: React.Dispatch<React.SetStateAction<boolean>>;

  // Operations
  fetchProject: () => Promise<void>;
  debouncedFetchProject: () => void;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useProjectData({
  projectId,
  isOpen,
  onProjectLoad,
  onError,
}: UseProjectDataOptions): UseProjectDataReturn {
  // ============================================================================
  // Core State
  // ============================================================================

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contentLoaded, setContentLoaded] = useState(false);

  // ============================================================================
  // Tasks State
  // ============================================================================

  const [openTasks, setOpenTasks] = useState<Task[]>([]);
  const [completedTasks, setCompletedTasks] = useState<Task[]>([]);
  const [assigneeNames, setAssigneeNames] = useState<Record<string, string>>({});

  // ============================================================================
  // Recently Saved Protection
  // ============================================================================

  const [recentlySaved, setRecentlySaved] = useState(false);
  const recentlySavedTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ============================================================================
  // Fetching Guards
  // ============================================================================

  const isFetchingProject = useRef(false);
  const [fetchProjectTimeoutId, setFetchProjectTimeoutId] = useState<NodeJS.Timeout | null>(null);

  // ============================================================================
  // Modal State Tracking
  // ============================================================================

  const [hasOpened, setHasOpened] = useState(false);

  // ============================================================================
  // Fetch Project Function
  // ============================================================================

  const fetchProject = useCallback(async () => {
    if (!projectId) return;

    // Prevent duplicate concurrent calls
    if (isFetchingProject.current) {
      return;
    }

    // Skip fetchProject if we recently saved to prevent overwriting manual changes
    if (recentlySaved) {
      return;
    }

    // Set the fetching flag
    isFetchingProject.current = true;

    // Only show loading if we don't have project data yet
    if (!project) {
      setLoading(true);
    }

    try {
      // Fetch project data with proper team member joins
      const { data: projectData, error: projectError } = await supabase
        .from("projects")
        .select(
          `
          *,
          new_project_form_completed,
          create_design_now,
          creator:assigned_sales_rep_id(email, first_name, last_name, avatar_url),
          sales_rep:assigned_sales_rep_id(id, email, first_name, last_name, phone_number, avatar_url),
          internal_pm:assigned_internal_pm_id(id, email, first_name, last_name, phone_number, avatar_url),
          installer_partner:assigned_installer_partner_id(id, company_name, user_id),
          biz_dev_rep:assigned_biz_dev_rep_id(id, email, first_name, last_name, phone_number, avatar_url),
          stages:current_stage_id(id, name, pipeline_id, stage_order),
          owner:assigned_sales_rep_id (
            id,
            email,
            first_name,
            last_name,
            phone_number,
            avatar_url
          ),
          closer:assigned_closer_id(id, first_name, last_name, email, phone_number),
          setter:assigned_setter_id(id, first_name, last_name, email, phone_number),
          profit_per_watt,
          total_project_profit,
          allowed_commission_per_watt,
          total_commission
        `
        )
        .eq("id", projectId)
        .single();

      if (projectError) {
        console.error("Error fetching project data:", projectError);
        throw projectError;
      }

      // Fetch open tasks for this project
      supabase
        .from("tasks")
        .select(
          `
          id,
          title,
          description,
          status,
          priority,
          due_date,
          user_id,
          assignee_id,
          project_id,
          created_at,
          updated_at,
          completed_at
        `
        )
        .eq("project_id", projectId)
        .neq("status", "completed")
        .then(({ data: tasksData, error: tasksError }) => {
          if (tasksError) {
            console.error("Error fetching tasks:", tasksError);
            return;
          }
          if (tasksData) {
            const tasks = tasksData as Task[];
            setOpenTasks(tasks);

            // Fetch assignee names
            const assigneeIds = Array.from(
              new Set(tasks.map((task) => task.assignee_id).filter(Boolean))
            );
            if (assigneeIds.length > 0) {
              supabase
                .from("users")
                .select("id, first_name, last_name, email")
                .in("id", assigneeIds)
                .then(({ data: usersData }) => {
                  if (usersData) {
                    const names: Record<string, string> = {};
                    usersData.forEach((user) => {
                      names[user.id] =
                        user.first_name && user.last_name
                          ? `${user.first_name} ${user.last_name}`
                          : user.first_name ||
                            user.last_name ||
                            user.email?.split("@")[0] ||
                            "";
                    });
                    setAssigneeNames(names);
                  }
                });
            }
          }
        });

      // Fetch completed tasks for this project
      supabase
        .from("tasks")
        .select(
          `
          id,
          title,
          description,
          status,
          priority,
          due_date,
          user_id,
          assignee_id,
          project_id,
          created_at,
          updated_at,
          completed_at
        `
        )
        .eq("project_id", projectId)
        .eq("status", "completed")
        .then(({ data: completedTasksData, error: completedTasksError }) => {
          if (completedTasksError) {
            console.error("Error fetching completed tasks:", completedTasksError);
            return;
          }
          if (completedTasksData) {
            const completedTasksList = completedTasksData as Task[];
            setCompletedTasks(completedTasksList);
          }
        });

      // Fetch team name for sales rep (project owner)
      let teamName = "";
      if (projectData.assigned_sales_rep_id) {
        try {
          const { data: teamData, error: teamError } = await supabase
            .from("user_teams")
            .select("team_id")
            .eq("user_id", projectData.assigned_sales_rep_id)
            .limit(1)
            .maybeSingle();

          if (!teamError && teamData) {
            const { data: teamDetails, error: teamDetailsError } = await supabase
              .from("teams")
              .select("name")
              .eq("id", teamData.team_id)
              .single();

            if (!teamDetailsError && teamDetails) {
              teamName = teamDetails.name;
            }
          }
        } catch (err) {
          console.error("Error fetching team information:", err);
        }
      }

      // Format the project data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const formattedProject: any = {
        ...projectData,
        team_name: teamName,
        utility_bill_confirmed: projectData.utility_bill_confirmed ? "Yes" : "No",
        drivers_licenses_confirmed: projectData.drivers_licenses_confirmed ? "Yes" : "No",
        owner_ui: projectData.owner || projectData.sales_rep,
        sales_rep_ui: projectData.sales_rep,
        internal_pm_ui: projectData.internal_pm,
        installer_partner_ui: projectData.installer_partner,
        biz_dev_rep_ui: projectData.biz_dev_rep,
        closer: projectData.closer,
        setter: projectData.setter,
        status: projectData.status || projectData.project_status || "Unknown",
        module_manufacturer: projectData.module_manufacturer,
        project_street: projectData.project_street || "",
        project_city: projectData.project_city || "",
        project_state: projectData.project_state || "",
        project_zip_code: projectData.project_zip_code || "",
        project_county: projectData.project_county || "",
        new_project_form_completed: projectData.new_project_form_completed || false,
        create_design_now: projectData.create_design_now || null,
      } as unknown as Project;

      // Update local state
      setProject(formattedProject);

      // Notify parent to update form state
      onProjectLoad?.(formattedProject as ExtendedProject);
    } catch (err) {
      console.error("Error loading project details:", err);
      const errorMessage = `Failed to load project details: ${err instanceof Error ? err.message : String(err)}`;
      setError(errorMessage);
      onError?.(new Error(errorMessage));
    } finally {
      setLoading(false);
      // Reset the fetching flag
      isFetchingProject.current = false;
    }
  }, [projectId, project, recentlySaved, onProjectLoad, onError]);

  // ============================================================================
  // Debounced Fetch
  // ============================================================================

  const debouncedFetchProject = useCallback(() => {
    if (fetchProjectTimeoutId) {
      clearTimeout(fetchProjectTimeoutId);
    }
    const timeoutId = setTimeout(() => {
      fetchProject();
    }, 150);
    setFetchProjectTimeoutId(timeoutId);
  }, [fetchProjectTimeoutId, fetchProject]);

  // ============================================================================
  // Real-time Subscription
  // ============================================================================

  useEffect(() => {
    if (!projectId || !isOpen) return;

    const channel = supabase
      .channel(`project_modal_${projectId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "projects",
          filter: `id=eq.${projectId}`,
        },
        (payload) => {
          // Check if this is a recent event (within last 10 seconds) to avoid stale events
          const eventTime = new Date(payload.commit_timestamp).getTime();
          const now = Date.now();
          const timeDiff = now - eventTime;

          if (timeDiff > 10000) {
            return;
          }

          // Use debounced fetch but only if we haven't recently saved
          if (!recentlySaved) {
            debouncedFetchProject();
          }
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [projectId, isOpen, recentlySaved, debouncedFetchProject]);

  // ============================================================================
  // Reset Flags When Modal Closes
  // ============================================================================

  useEffect(() => {
    if (!isOpen) {
      isFetchingProject.current = false;
    }
  }, [isOpen]);

  // ============================================================================
  // Return Hook Value
  // ============================================================================

  return {
    // Core data
    project,
    setProject,
    loading,
    setLoading,
    error,
    setError,
    contentLoaded,
    setContentLoaded,

    // Tasks data
    openTasks,
    setOpenTasks,
    completedTasks,
    setCompletedTasks,
    assigneeNames,
    setAssigneeNames,

    // Recently saved protection
    recentlySaved,
    setRecentlySaved,
    recentlySavedTimeoutRef,

    // Fetching guards
    isFetchingProject,
    fetchProjectTimeoutId,
    setFetchProjectTimeoutId,

    // Modal state tracking
    hasOpened,
    setHasOpened,

    // Operations
    fetchProject,
    debouncedFetchProject,
  };
}
